// welcomeHome.ts — Patch 015.12. Welcome Home package (staff side).
//
// Per-project pre-occupancy checklist. Backend: welcome_home_items
// (migration 0063) + routes/welcome_home.ts. The staff surface seeds the
// address-keyed template, tracks the occupancy-date anchor, and shows the
// per-group completion roll-up the client drives from the portal.

import { request } from './request'

export type WelcomeHomeItemGroup =
  | 'utilities'
  | 'address_change'
  | 'local'
  | 'heritage'

export interface WelcomeHomeItem {
  id: string
  project_id: string
  item_group: WelcomeHomeItemGroup
  display_name: string
  description: string | null
  action_url: string | null
  target_date: string | null
  completed: number
  completed_at: string | null
  completed_by: string | null
  sort_order: number
  created_at: string
  updated_at: string | null
}

export interface WelcomeHomeGroupCount {
  total: number
  completed: number
}

export interface WelcomeHomeSummary {
  total: number
  completed: number
  groups: Record<WelcomeHomeItemGroup, WelcomeHomeGroupCount>
}

export interface WelcomeHomeListResponse {
  ok: true
  items: WelcomeHomeItem[]
  summary: WelcomeHomeSummary
  target_occupancy_date: string | null
}

export interface SeedWelcomeHomeResponse {
  ok: true
  seeded: number
  skipped: number
  total: number
  city_matched: boolean
  request_id: string
}

export interface CreateWelcomeHomeItemInput {
  item_group: WelcomeHomeItemGroup
  display_name: string
  description?: string
  action_url?: string
  target_date?: string
}

export interface PatchWelcomeHomeItemInput {
  display_name?: string
  description?: string
  action_url?: string
  target_date?: string | null
  completed?: boolean
  archived?: boolean
}

export interface WelcomeHomeCadenceResponse {
  ok: true
  scanned_projects: number
  milestones_detected: number
  drafted: number
  skipped_no_occupancy: number
  skipped_no_recipient_email: number
  errors: Array<{ project_id: string; error: string }>
}

const pid = (id: string): string => encodeURIComponent(id)

export const welcomeHomeApi = {
  list: (projectId: string) =>
    request<WelcomeHomeListResponse>(
      `/api/projects/${pid(projectId)}/welcome-home-items`,
    ),
  seedTemplate: (projectId: string) =>
    request<SeedWelcomeHomeResponse>(
      `/api/projects/${pid(projectId)}/welcome-home/seed-template`,
      { method: 'POST', body: '{}' },
    ),
  setOccupancyDate: (projectId: string, targetOccupancyDate: string | null) =>
    request<{
      ok: true
      project_id: string
      target_occupancy_date: string | null
    }>(`/api/projects/${pid(projectId)}/welcome-home/occupancy-date`, {
      method: 'PATCH',
      body: JSON.stringify({ target_occupancy_date: targetOccupancyDate }),
    }),
  createItem: (projectId: string, body: CreateWelcomeHomeItemInput) =>
    request<{ ok: true; id: string; request_id: string }>(
      `/api/projects/${pid(projectId)}/welcome-home-items`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateItem: (
    projectId: string,
    itemId: string,
    body: PatchWelcomeHomeItemInput,
  ) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${pid(projectId)}/welcome-home-items/${pid(itemId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  runCadence: () =>
    request<WelcomeHomeCadenceResponse>('/api/welcome-home/cadence/run', {
      method: 'POST',
      body: '{}',
    }),
}
